/**
 * RaceConfigForm Component
 *
 * Configuration form for creating a new race room.
 * Allows host to set: allowed apps, max participants, question count.
 */

import { useState } from 'react';
import { TOKO_APPS } from '@/config/apps';
import type { RaceConfig } from '@/types/race';
import { DEFAULT_RACE_CONFIG } from '@/types/race';
import styles from './Race.module.css';

interface RaceConfigFormProps {
  onSubmit: (config: RaceConfig) => void;
  isLoading?: boolean;
}

export function RaceConfigForm({ onSubmit, isLoading }: RaceConfigFormProps) {
  const [allowedApps, setAllowedApps] = useState<string[]>(DEFAULT_RACE_CONFIG.allowedApps);
  const [maxParticipants, setMaxParticipants] = useState(DEFAULT_RACE_CONFIG.maxParticipants);
  const [questionCount, setQuestionCount] = useState(DEFAULT_RACE_CONFIG.questionCount);

  const handleAppToggle = (appId: string) => {
    setAllowedApps((prev) => {
      if (prev.includes(appId)) {
        // Don't allow deselecting all apps
        if (prev.length === 1) return prev;
        return prev.filter((id) => id !== appId);
      }
      return [...prev, appId];
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      allowedApps,
      maxParticipants,
      questionCount,
    });
  };

  return (
    <div className={styles.configContainer}>
      <h1 className={styles.pageTitle}>Host a Race</h1>
      <p className={styles.pageSubtitle}>Configure your race settings and invite students to join.</p>

      <form onSubmit={handleSubmit} className={styles.configForm}>
        {/* Allowed Apps */}
        <div className={styles.formSection}>
          <label className={styles.formLabel}>Allowed Apps</label>
          <p className={styles.formHint}>Select which apps can join this race</p>
          <div className={styles.appCheckboxes}>
            {TOKO_APPS.filter((app) => app.id !== 'addition-plus').map((app) => (
              <label
                key={app.id}
                className={`${styles.appCheckbox} ${allowedApps.includes(app.id) ? styles.appCheckboxSelected : ''}`}
                style={{ '--app-color': app.color } as React.CSSProperties}
              >
                <input
                  type="checkbox"
                  checked={allowedApps.includes(app.id)}
                  onChange={() => handleAppToggle(app.id)}
                  className={styles.hiddenCheckbox}
                />
                <span className={styles.appCheckboxIcon}>
                  {app.id.startsWith('addition') ? '+' : '\u00d7'}
                </span>
                <span className={styles.appCheckboxLabel}>{app.shortName}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Question Count */}
        <div className={styles.formSection}>
          <label className={styles.formLabel}>Number of Questions</label>
          <p className={styles.formHint}>How many questions each participant must answer</p>
          <div className={styles.sliderContainer}>
            <input
              type="range"
              min={10}
              max={100}
              step={5}
              value={questionCount}
              onChange={(e) => setQuestionCount(Number(e.target.value))}
              className={styles.slider}
            />
            <span className={styles.sliderValue}>{questionCount}</span>
          </div>
        </div>

        {/* Max Participants */}
        <div className={styles.formSection}>
          <label className={styles.formLabel}>Max Participants</label>
          <p className={styles.formHint}>Maximum number of students who can join (1-100)</p>
          <input
            type="number"
            min={1}
            max={100}
            value={maxParticipants}
            onChange={(e) => setMaxParticipants(Math.min(100, Math.max(1, Number(e.target.value))))}
            className={styles.numberInput}
          />
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className={styles.primaryButton}
          disabled={isLoading || allowedApps.length === 0}
        >
          {isLoading ? 'Creating...' : 'Create Race'}
        </button>
      </form>
    </div>
  );
}
